/**
 * Onboarding wizard page — a single-page React experience driving the
 * 7-step org activation flow. Each step component is split into its
 * own file in ./onboarding/ but they all read/write through this
 * shell so the saved-state plumbing stays in one place.
 *
 * Resume contract: the wizard reads the canonical step catalogue from
 * /api/onboarding/steps on mount so a backend STEPS change propagates
 * without a frontend release.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import authedFetch from '../lib/authedFetch';
import { toast } from 'sonner';
import {
  Sparkles, Check, Loader2, Share2, FileDown, AlertTriangle,
  ChevronRight, ChevronLeft, Rocket, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import PageShell from '../components/layout/PageShell';
import PageHero from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

import StepIdentity from './onboarding/StepIdentity';
import StepVerticalScale from './onboarding/StepVerticalScale';
import StepOperatingNumbers from './onboarding/StepOperatingNumbers';
import StepKpisOfInterest from './onboarding/StepKpisOfInterest';
import StepMaturity from './onboarding/StepMaturity';
import StepDataSources from './onboarding/StepDataSources';
import StepReviewActivate from './onboarding/StepReviewActivate';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_COMPONENTS = {
  identity: StepIdentity,
  vertical_scale: StepVerticalScale,
  operating_numbers: StepOperatingNumbers,
  kpis_of_interest: StepKpisOfInterest,
  maturity: StepMaturity,
  data_sources: StepDataSources,
  review_activate: StepReviewActivate,
};

export default function OnboardingWizardPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orgId = params.orgId;
  // Share token may arrive as a path param (/onboarding/share/:token) or query (?token=…).
  const shareToken = params.token || searchParams.get('token');

  const [steps, setSteps] = useState([]);
  const [session, setSession] = useState(null);
  const [activeStepId, setActiveStepId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);

  const pageTheme = useMemo(() => getLightSectionTheme('Indigo'), []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const stepsRes = await authedFetch(`${API}/api/onboarding/steps`);
      const stepsData = stepsRes.ok ? await stepsRes.json() : { steps: [] };
      setSteps(stepsData.steps || []);

      let sessionData = null;
      if (shareToken) {
        // Public mode — fetch the customer-scoped resume payload.
        const r = await fetch(`${API}/api/onboarding/share/${shareToken}`);
        if (!r.ok) throw new Error('Invalid or revoked link');
        const data = await r.json();
        sessionData = data.session;
        setSteps(data.steps || stepsData.steps || []);
        setOrg(data.org);
      } else {
        const r = await authedFetch(`${API}/api/onboarding/${orgId}`);
        if (!r.ok) throw new Error('Failed to load onboarding session');
        const data = await r.json();
        if (!data.session) {
          // Start one on first visit.
          const startRes = await authedFetch(`${API}/api/onboarding/start`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ org_id: orgId }),
          });
          sessionData = (await startRes.json()).session;
        } else {
          sessionData = data.session;
        }
      }
      setSession(sessionData);

      // Default-open the first incomplete step.
      const firstIncomplete = (stepsData.steps || []).find(
        (s) => (sessionData?.steps?.[s.id]?.status || 'pending') !== 'complete',
      );
      setActiveStepId(firstIncomplete?.id || (stepsData.steps?.[0]?.id ?? null));
    } catch (e) {
      toast.error(e.message || 'Failed to load onboarding');
    } finally {
      setLoading(false);
    }
  }, [orgId, shareToken]);

  useEffect(() => { reload(); }, [reload]);

  const handleSaveStep = async (stepId, answers, { markComplete = true } = {}) => {
    setSaving(true);
    try {
      const url = shareToken
        ? `${API}/api/onboarding/share/${shareToken}/step/${stepId}`
        : `${API}/api/onboarding/${orgId}/step/${stepId}`;
      const opts = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers, mark_complete: markComplete }),
      };
      const res = shareToken ? await fetch(url, opts) : await authedFetch(url, opts);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSession(data.session);
      if (markComplete) {
        toast.success(`Saved · ${data.completion_pct}% complete`);
        // Jump to next incomplete step.
        const next = steps.find((s) => (data.session?.steps?.[s.id]?.status || 'pending') !== 'complete');
        if (next && next.id !== stepId) setActiveStepId(next.id);
      }
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (shareToken) return;
    setSaving(true);
    try {
      const res = await authedFetch(`${API}/api/onboarding/${orgId}/complete`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Org activated. Welcome to live mode.');
      navigate('/admin');
    } catch (e) {
      toast.error(`Activate failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleShareLink = async () => {
    if (shareToken) return;
    try {
      const res = await authedFetch(`${API}/api/onboarding/${orgId}/share`, { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const url = `${window.location.origin}/onboarding/share/${data.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Resume link copied — share with your customer');
    } catch (e) {
      toast.error(`Share failed: ${e.message || e}`);
    }
  };

  const handleDownloadChecklist = async () => {
    const res = await authedFetch(`${API}/api/onboarding/data-readiness-checklist`);
    const md = await res.text();
    const blob = new Blob([md], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'Seertech_Data_Readiness_Checklist.md';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const stepsState = session?.steps || {};
  const completedCount = steps.filter((s) => stepsState[s.id]?.status === 'complete').length;
  const requiredSteps = steps.filter((s) => s.required);
  const requiredDone = requiredSteps.filter((s) => stepsState[s.id]?.status === 'complete').length;
  const completionPct = requiredSteps.length
    ? Math.round((100 * requiredDone) / requiredSteps.length)
    : 100;
  const allRequiredDone = requiredDone === requiredSteps.length;

  const ActiveStep = activeStepId ? STEP_COMPONENTS[activeStepId] : null;
  const activeStepDef = steps.find((s) => s.id === activeStepId);
  const activeStepState = stepsState[activeStepId] || { answers: {} };

  if (loading) {
    return (
      <PageShell theme={pageTheme}>
        <div className="px-4 py-16 text-center text-slate-500">
          <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading onboarding wizard…
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      theme={pageTheme}
      hero={(
        <PageHero
          icon={Sparkles}
          theme={pageTheme}
          title={shareToken ? 'Complete your Seertech onboarding' : `Onboard ${org?.name || 'this org'}`}
          description={
            shareToken
              ? 'Your Seertech contact shared this link so you can fill in the customer-owned sections of onboarding. Your progress saves automatically.'
              : 'A guided checklist that sets every initial value your ROI waterfall needs, from industry profile to KPI baselines. Each answer audits into Coefficient History.'
          }
          stats={[
            { label: 'Required steps done', value: `${requiredDone}/${requiredSteps.length}` },
            { label: 'Overall', value: `${completedCount}/${steps.length}` },
            { label: 'Completion', value: `${completionPct}%` },
            { label: 'Next', value: steps.find((s) => stepsState[s.id]?.status !== 'complete')?.title || 'Done' },
          ]}
          testId="onboarding-hero"
        />
      )}
      data-testid="onboarding-wizard-page"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 py-8 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar — step list */}
        <aside className="space-y-1.5" data-testid="onboarding-step-list">
          {steps.map((s) => {
            const state = stepsState[s.id] || {};
            const isActive = s.id === activeStepId;
            const isDone = state.status === 'complete';
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveStepId(s.id)}
                data-testid={`onboarding-step-tab-${s.id}`}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${
                  isActive
                    ? 'border-indigo-400 bg-indigo-50 ring-1 ring-indigo-200'
                    : isDone
                      ? 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-indigo-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`shrink-0 h-6 w-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                    isDone ? 'bg-emerald-500 text-white' : isActive ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {isDone ? <Check className="h-3 w-3" /> : s.order}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs font-semibold truncate ${isActive ? 'text-indigo-900' : 'text-slate-900'}`}>
                      {s.title}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {s.required ? 'Required' : 'Optional'} · {s.audience === 'sales' ? 'Sales' : s.audience === 'customer' ? 'Customer' : 'Shared'}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
          {!shareToken && (
            <div className="pt-4 space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleShareLink}
                data-testid="onboarding-share-link-btn"
              >
                <Share2 className="h-3.5 w-3.5 mr-2" /> Copy customer resume link
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleDownloadChecklist}
                data-testid="onboarding-download-checklist-btn"
              >
                <FileDown className="h-3.5 w-3.5 mr-2" /> Data Readiness Checklist
              </Button>
              {allRequiredDone && (
                <Button
                  className="w-full justify-center bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={handleActivate}
                  disabled={saving}
                  data-testid="onboarding-activate-btn"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Rocket className="h-3.5 w-3.5 mr-2" />}
                  Activate org
                </Button>
              )}
            </div>
          )}
        </aside>

        {/* Body — active step */}
        <main className="space-y-4" data-testid={`onboarding-step-body-${activeStepId || 'none'}`}>
          {activeStepDef && ActiveStep && (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 bg-white p-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-indigo-600">
                    Step {activeStepDef.order} of {steps.length} · {activeStepDef.required ? 'Required' : 'Optional'}
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mt-0.5">{activeStepDef.title}</h2>
                  <p className="text-sm text-slate-600 mt-1">{activeStepDef.description}</p>
                </div>
                {activeStepDef.writes_org_override && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800 flex items-center gap-1.5 max-w-xs">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    Writes to your org override layer — audited to Coefficient History.
                  </div>
                )}
              </div>

              <ActiveStep
                orgId={orgId}
                shareToken={shareToken}
                step={activeStepDef}
                state={activeStepState}
                onSave={(answers, opts) => handleSaveStep(activeStepDef.id, answers, opts)}
                saving={saving}
              />

              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const idx = steps.findIndex((s) => s.id === activeStepDef.id);
                    if (idx > 0) setActiveStepId(steps[idx - 1].id);
                  }}
                  disabled={steps.findIndex((s) => s.id === activeStepDef.id) === 0}
                  data-testid="onboarding-prev-btn"
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Previous
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const idx = steps.findIndex((s) => s.id === activeStepDef.id);
                    if (idx < steps.length - 1) setActiveStepId(steps[idx + 1].id);
                  }}
                  disabled={steps.findIndex((s) => s.id === activeStepDef.id) === steps.length - 1}
                  data-testid="onboarding-next-btn"
                >
                  Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </>
          )}
        </main>
      </div>
    </PageShell>
  );
}
